/**
 * @graphql cc
 */

import React, { useContext, useCallback, useRef, useEffect } from 'react';
import gql from 'graphql-tag';
import type { MutationUpdaterFn } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { PageCommentEditor } from '@confluence/comment-simple/entry-points/PageCommentEditor.ts';
import type { CommentSaveHandler } from '@confluence/comment-simple/entry-points/commentSimpleTypes.ts';
import {
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { GraphQLContentStatus } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralCommentMutation.ts';
import { updateApolloCacheParentCallback } from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	CommentType,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	useCreateObjectCommentMutation,
	addEmptyObjectRepliesArray,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import { type useCreateObjectCommentMutation$data } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutationTypes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import {
	useCommentsPanelActions,
	AddFooterCommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	getTranslatedError,
	isCreateNotPermittedError,
	isViewNotPermittedError,
} from '@confluence/comments-util/entry-points/objectCommentsErrorUtils.ts';
import {
	handleFooterCommentsNavigation,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';

import type {
	ObjectAddCommentEditorCreateGeneralCommentMutation as ObjectAddCommentEditorCreateGeneralCommentMutation$data,
	ObjectAddCommentEditorCreateGeneralCommentMutationVariables as ObjectAddCommentEditorCreateGeneralCommentMutation$variables,
} from './__types__/ObjectAddCommentEditorCreateGeneralCommentMutation.ts';

const ObjectAddCommentEditorCreateGeneralCommentMutation = gql`
	mutation ObjectAddCommentEditorCreateGeneralCommentMutation(
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;

export interface ObjectAddCommentEditorProps {
	isOffline?: boolean;
	setAutoFocusPromptRef?: (value: boolean) => void;
	handleCancel?: () => void;
}

export const ObjectAddCommentEditor = ({
	isOffline,
	setAutoFocusPromptRef,
	handleCancel,
}: ObjectAddCommentEditorProps) => {
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { closeCommentContainer } = useObjectCommentDispatchContext();
	const { footerEditorEntry } = useObjectCommentContext();
	const [contentId] = usePageContentId();
	const [{ searchSessionId }] = useSearchSessionId();

	const { setCurrentlySelectedThreadKey, setAddFooterCommentAdf } = useCommentsPanelActions();
	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { addNewCommentThreads } = useCommentsDataActions();
	const pageMode = useGetPageMode();
	const [contentType] = useContentType();
	const { cloudId } = useSessionData();

	// Ref to store editor actions for focusing when sticky draft is clicked
	const editorActionsRef = useRef<any>(null);

	// Focus editor when opened from sticky draft (handles case when editor is already mounted)
	useEffect(() => {
		if (
			footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT &&
			editorActionsRef.current?.focus
		) {
			editorActionsRef.current.focus();
		}
	}, [footerEditorEntry]);

	const [createGeneralCommentFn] = useMutationInterop<
		ObjectAddCommentEditorCreateGeneralCommentMutation$data,
		ObjectAddCommentEditorCreateGeneralCommentMutation$variables
	>(
		// @ts-expect-error -- http://go/cfe-use-relay-artifacts
		ObjectAddCommentEditorCreateGeneralCommentMutation,
	);

	const { addCommentInCache } = useAllCommentsData({
		contentId: contentId || '',
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const createObjectCommentMutationFn = useCreateObjectCommentMutation();

	const clearAddFooterCommentAdf = useCallback(() => {
		setAddFooterCommentAdf(undefined);
	}, [setAddFooterCommentAdf]);

	const failPublishExperience = useCallback(
		(error: Error) => {
			// TODO: show an error message to the user for the error
			// Mark the experience as failed/aborted and include the error details
			experienceTracker.stopOnError({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);

	const updateNewCommentInCache: MutationUpdaterFn<useCreateObjectCommentMutation$data> =
		useCallback(
			(_dataProxy, mutationResult) => {
				addCommentInCache({
					newComment: addEmptyObjectRepliesArray(mutationResult.data!.createFooterComment),
					onError: failPublishExperience,
				});
			},
			[addCommentInCache, failPublishExperience],
		);

	const handleCommentsPanelSave: CommentSaveHandler = (adfObject: any, onSuccess: any) => {
		const pageId = contentId || '';
		const { adf } = adfObject;

		startEditorSubExperience({
			name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});

		const mutationVariables = {
			cloudId,
			input: {
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
				},
				commentSource: Platform.WEB,
				containerId: pageId,
			},
		};

		return (
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? createObjectCommentMutationFn({
						variables: {
							...mutationVariables,
							pageId,
						},
						// @ts-ignore -- can't import type directly in update FN because it'd be circular
						update: updateNewCommentInCache,
					})
				: createGeneralCommentFn({
						variables: mutationVariables,
						// @ts-ignore -- can't import type directly in update FN because it'd be circular
						update: updateApolloCacheParentCallback(
							'create',
							{
								pageId,
								contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
								type: ['FOOTER'],
							},
							isReactionsEnabled,
						),
					})
		)
			.then(({ data }: any) => {
				const commentResult = data.createFooterComment;
				const comment: CommentData = {
					...commentResult,
					replies: [], // not included in the payload
					isOpen: true,
					type: CommentType.GENERAL,
				};

				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					addNewCommentThreads({ inline: {}, general: { [comment.id]: comment } });
				}

				onSuccess && onSuccess();
				resetContentChanged();
				clearAddFooterCommentAdf();
				closeCommentContainer();

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'created',
						actionSubject: 'comment',
						actionSubjectId: comment.id, // The newly created comment ID
						objectType: contentType,
						objectId: contentId,
						source: AnalyticsSource.PAGE_FOOTER,
						attributes: {
							commentType: AnalyticsCommentType.PAGE,
							source: AnalyticsSource.PAGE_FOOTER,
							pageMode,
							isReply: false,
							pageType: contentType,
							isLivePage: pageMode === PageMode.LIVE,
							editor: 'v2',
							searchSessionId,
							navdexPointType: END,
							parentCommentId: null, // analytics event schema type expects string or null
							isStickyFooter: footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT, // Keeps track of where the actual create came from
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				});

				setCurrentlySelectedThreadKey(comment.id, CommentType.GENERAL);
				handleFooterCommentsNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					inline: 'center',
					commentId: comment.id,
				});
			})
			.catch((error: Error) => {
				if (fg('confluence_fe_add_page_comment_exp_coverage')) {
					const translatedError = getTranslatedError(error);

					if (
						isCreateNotPermittedError(translatedError) ||
						isViewNotPermittedError(translatedError)
					) {
						// TODO: show an error message to the user for create not permitted
						experienceTracker.abort({
							name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
							reason: translatedError.id,
						});
						// Abort parent experience too, since it's not automatically aborted when child is aborted.
						experienceTracker.abort({
							name: ADD_PAGE_COMMENT_EXPERIENCE,
							reason: translatedError.id,
						});
					} else {
						// TODO: show an error message to the user for the error
						failPublishExperience(error);
					}
				}
				if (fg('confluence_comments_error_handling_improvements')) {
					throw error;
				}
			});
	};

	const handleCancelInternal = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
		const doCancel = () => {
			if (isEditorAborted) {
				setAutoFocusPromptRef?.(true);
				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason: 'aborted because comment was cancelled',
				});
			}

			clearAddFooterCommentAdf();
			closeCommentContainer();
			handleCancel?.();
		};

		if (hasContentChanged) {
			resetContentChanged();
		}
		doCancel();
	};

	const handleOnEditorLoaded = () => {
		experienceTracker.succeed({
			name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
		});

		const footerCommentContainer = document.querySelector('[data-testId="footerCommentEditor"]');

		if (!footerCommentContainer) {
			return;
		}

		footerCommentContainer.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'nearest',
		});
	};

	const handleEditorReady = (editorActions: any) => {
		handleOnEditorLoaded();
		// Store editor actions for later use (e.g., focusing from sticky draft)
		editorActionsRef.current = editorActions;
		// Focus the editor when opened from sticky draft
		if (footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT && editorActions?.focus) {
			editorActions.focus();
		}
	};

	// This is debounced at the editor level (at 150ms) to prevent constant updates hurting performance
	const handleEditorContentChangedWithData = (hasContent: boolean, content?: any) => {
		// Store the actual ADF content for real-time sync
		if (hasContent && content) {
			setAddFooterCommentAdf(content);
		} else {
			setAddFooterCommentAdf(undefined);
		}

		// Also call the original onChange handler
		onChange(hasContent);
	};

	return (
		<PageCommentEditor
			mode="create"
			saveHandler={handleCommentsPanelSave}
			cancelHandler={handleCancelInternal}
			onEditorLoaded={handleOnEditorLoaded}
			onEditorReady={handleEditorReady}
			onEditorContentChangedWithData={handleEditorContentChangedWithData}
			useNewExperienceAbort
			expandEditor
			isFooterComment
			isOffline={isOffline}
		/>
	);
};
